import { useState } from 'react';
import { MallUser } from './MallAuthModal';

interface Props { lang: 'zh' | 'en'; user?: MallUser | null; onLoginClick?: () => void; }

const HOT_CAMS = [
  { name: { zh: '纽约时代广场', en: 'Times Square, NYC' }, icon: 'ri-building-line', color: '#FFD700' },
  { name: { zh: '东京涩谷十字路口', en: 'Shibuya Crossing, Tokyo' }, icon: 'ri-road-map-line', color: '#FF6B9D' },
  { name: { zh: '巴黎埃菲尔铁塔', en: 'Eiffel Tower, Paris' }, icon: 'ri-landscape-line', color: '#00FFCC' },
  { name: { zh: '迪拜哈利法塔', en: 'Burj Khalifa, Dubai' }, icon: 'ri-building-4-line', color: '#A855F7' },
  { name: { zh: '悉尼歌剧院', en: 'Sydney Opera House' }, icon: 'ri-music-2-line', color: '#FF6B35' },
  { name: { zh: '北京天安门广场', en: 'Tiananmen Square, Beijing' }, icon: 'ri-government-line', color: '#FF4081' },
];

export default function MallEarthSection({ lang, user, onLoginClick }: Props) {
  const [camModal, setCamModal] = useState(false);
  const [selectedCam, setSelectedCam] = useState<typeof HOT_CAMS[0] | null>(null);
  const [camPaying, setCamPaying] = useState(false);
  const [camActive, setCamActive] = useState(false);
  const [camSeconds, setCamSeconds] = useState(0);

  const handleCamSelect = (cam: typeof HOT_CAMS[0]) => {
    setSelectedCam(cam);
    setCamModal(true);
    setCamActive(false);
    setCamPaying(false);
  };

  const handleCamPay = () => {
    setCamPaying(true);
    setTimeout(() => {
      setCamPaying(false);
      setCamActive(true);
      setCamSeconds(60);
      const t = setInterval(() => {
        setCamSeconds(s => {
          if (s <= 1) { clearInterval(t); setCamActive(false); return 0; }
          return s - 1;
        });
      }, 1000);
    }, 2000);
  };

  return (
    <section id="earth" className="py-24 px-6 relative z-10" style={{ background: 'linear-gradient(180deg, #050A14 0%, #020810 100%)' }}>
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.25)' }}>
            <i className="ri-earth-line text-sm" style={{ color: '#FFD700' }} />
            <span className="text-sm font-bold" style={{ color: '#FFD700', fontFamily: 'Orbitron, sans-serif' }}>{lang === 'zh' ? 'Google Earth 星际视角' : 'Google Earth Stellar Perspective'}</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span style={{ background: 'linear-gradient(90deg, #FFD700, #00FFCC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{lang === 'zh' ? '地球视角 · 实时摄像头' : 'Earth View · Live Camera Feed'}</span>
          </h2>
          <p className="text-white/40 text-base max-w-3xl mx-auto">{lang === 'zh' ? '登录后，Google Earth将自动定位到你的家庭地址，实时查看配送路线。点击地球上任意位置，可查看该位置的实时公开摄像头画面（$1/分钟）。所有摄像头均为公开授权，严格保护隐私。' : 'After login, Google Earth auto-locates your home address. View real-time delivery routes. Click any location on Earth to view real-time public camera feeds ($1/min). All cameras are publicly authorized — privacy strictly protected.'}</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Google Earth Map */}
          <div className="xl:col-span-2 rounded-3xl overflow-hidden relative" style={{ height: '520px', border: '2px solid rgba(255,215,0,0.2)' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15000000!2d0!3d20!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%" height="100%" style={{ border: 0, filter: 'hue-rotate(180deg) saturate(1.5) brightness(0.75)' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Google Earth View"
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 70%, rgba(2,8,16,0.7) 100%)', borderRadius: '1.5rem' }} />
            {/* Live Badge */}
            <div className="absolute top-4 left-4 px-4 py-2 rounded-xl" style={{ background: 'rgba(1,2,8,0.88)', border: '1px solid rgba(255,215,0,0.35)', backdropFilter: 'blur(10px)' }}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs font-bold" style={{ color: '#FFD700', fontFamily: 'Orbitron, sans-serif' }}>GOOGLE EARTH · LIVE</span>
              </div>
            </div>
            {/* Home Locator Badge */}
            {user ? (
              <div className="absolute top-4 right-4 px-4 py-2 rounded-xl" style={{ background: 'rgba(1,2,8,0.88)', border: '1px solid rgba(0,255,204,0.35)', backdropFilter: 'blur(10px)' }}>
                <div className="flex items-center gap-2">
                  <i className="ri-home-4-line text-xs" style={{ color: '#00FFCC' }} />
                  <div>
                    <div className="text-xs font-bold" style={{ color: '#00FFCC', fontFamily: 'Orbitron, sans-serif' }}>{lang === 'zh' ? '已定位到你的家' : 'HOME LOCATED'}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>{user.address}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute top-4 right-4 px-4 py-2 rounded-xl cursor-pointer transition-all hover:scale-105" onClick={onLoginClick} style={{ background: 'rgba(1,2,8,0.88)', border: '1px solid rgba(255,215,0,0.35)', backdropFilter: 'blur(10px)' }}>
                <div className="flex items-center gap-2">
                  <i className="ri-login-circle-line text-xs" style={{ color: '#FFD700' }} />
                  <span className="text-xs font-bold" style={{ color: '#FFD700', fontFamily: 'Orbitron, sans-serif' }}>{lang === 'zh' ? '登录以定位到你的家' : 'LOGIN TO LOCATE HOME'}</span>
                </div>
              </div>
            )}
            {/* Bottom CTA */}
            <div className="absolute bottom-4 left-4 right-4">
              <button onClick={() => { setSelectedCam(null); setCamModal(true); }} className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-[1.02]" style={{ background: 'rgba(255,215,0,0.15)', border: '1.5px solid rgba(255,215,0,0.4)', color: '#FFD700', backdropFilter: 'blur(10px)' }}>
                <i className="ri-camera-line" />{lang === 'zh' ? '点击地球任意位置查看实时摄像头 · $1/分钟' : 'Click Any Location for Live Camera · $1/min'}
              </button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex flex-col gap-4">
            {/* Hot Cameras */}
            <div className="p-5 rounded-2xl flex-1" style={{ background: 'rgba(5,10,20,0.95)', border: '1px solid rgba(255,215,0,0.15)' }}>
              <h3 className="text-white font-black text-base mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>{lang === 'zh' ? '热门实时摄像头' : 'Popular Live Cameras'}</h3>
              <div className="flex flex-col gap-2">
                {HOT_CAMS.map((cam, i) => (
                  <button key={i} onClick={() => handleCamSelect(cam)} className="w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.02]" style={{ background: `${cam.color}08`, border: `1px solid ${cam.color}20` }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${cam.color}15` }}>
                      <i className={`${cam.icon} text-sm`} style={{ color: cam.color }} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-white text-sm font-bold">{cam.name[lang]}</div>
                      <div className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{lang === 'zh' ? '公开授权 · 实时画面' : 'Public Auth · Live Feed'}</div>
                    </div>
                    <span className="text-xs font-black" style={{ color: cam.color, fontFamily: 'Orbitron, sans-serif' }}>$1/min</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="p-5 rounded-2xl" style={{ background: 'rgba(5,10,20,0.95)', border: '1px solid rgba(255,107,53,0.2)' }}>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-shield-check-line" style={{ color: '#FF6B35' }} />
                <span className="text-white font-bold text-sm">{lang === 'zh' ? '隐私保护声明' : 'Privacy Protection Statement'}</span>
              </div>
              <p className="text-white/40 text-xs leading-relaxed">{lang === 'zh' ? '所有摄像头均为公开授权的公共场所摄像头，不涉及任何私人住宅或私密场所。严格遵守GDPR及各国隐私法规，保护用户隐私安全。所有画面均经过AI隐私过滤处理。' : 'All cameras are publicly authorized public-space cameras only. No private residences or sensitive locations. Strictly GDPR compliant. All footage processed through AI privacy filtering.'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {camModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }}>
          <div className="w-full max-w-lg rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #060C18, #080E1C)', border: '2px solid rgba(255,215,0,0.3)' }}>
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,215,0,0.15)', border: '2px solid rgba(255,215,0,0.4)' }}>
                  <i className="ri-camera-line text-xl" style={{ color: '#FFD700' }} />
                </div>
                <div>
                  <h3 className="text-white font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif' }}>{selectedCam ? selectedCam.name[lang] : (lang === 'zh' ? '实时公开摄像头' : 'Live Public Camera')}</h3>
                  <p className="text-xs" style={{ color: 'rgba(255,215,0,0.5)' }}>{lang === 'zh' ? '公开授权 · 严格隐私保护' : 'Publicly Authorized · Strict Privacy Protection'}</p>
                </div>
              </div>
              {!camActive ? (
                <>
                  <div className="p-4 rounded-2xl mb-5" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)' }}>
                    <div className="flex justify-between items-center">
                      <span className="text-white/50 text-sm">{lang === 'zh' ? '计费方式' : 'Billing'}</span>
                      <span className="font-black text-2xl" style={{ color: '#FFD700', fontFamily: 'Orbitron, sans-serif' }}>$1 / {lang === 'zh' ? '分钟' : 'min'}</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl mb-6" style={{ background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.2)' }}>
                    <div className="flex items-start gap-2">
                      <i className="ri-shield-check-line text-sm mt-0.5" style={{ color: '#FF6B35' }} />
                      <p className="text-white/40 text-xs leading-relaxed">{lang === 'zh' ? '所有摄像头均为公开授权的公共场所摄像头，不涉及任何私人住宅或私密场所。严格遵守GDPR及各国隐私法规。' : 'All cameras are publicly authorized public-space cameras only. No private residences or sensitive locations. Strictly GDPR compliant.'}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => { setCamModal(false); setCamActive(false); }} className="flex-1 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}>{lang === 'zh' ? '取消' : 'Cancel'}</button>
                    <button onClick={handleCamPay} className="flex-1 py-3 rounded-xl font-black text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,107,53,0.25))', border: '2px solid rgba(255,215,0,0.5)', color: '#FFD700', fontFamily: 'Orbitron, sans-serif', fontSize: '0.8rem' }}>
                      {camPaying ? (lang === 'zh' ? '连接中...' : 'Connecting...') : (lang === 'zh' ? '开始观看 · $1/分钟' : 'START WATCHING · $1/min')}
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <div className="rounded-2xl overflow-hidden mb-4" style={{ height: '200px', background: '#000', border: '1px solid rgba(255,215,0,0.3)' }}>
                    <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0f1e, #050a14)' }}>
                      <div className="text-center">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse mx-auto mb-3" />
                        <div className="text-white/60 text-sm font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>LIVE FEED ACTIVE</div>
                        <div className="text-white/30 text-xs mt-1">{selectedCam ? selectedCam.name[lang] : (lang === 'zh' ? '实时画面传输中' : 'Real-time feed streaming')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl mb-4" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.2)' }}>
                    <span className="text-white/50 text-sm">{lang === 'zh' ? '剩余时间' : 'Time Remaining'}</span>
                    <span className="font-black text-2xl" style={{ color: '#FFD700', fontFamily: 'Orbitron, sans-serif' }}>{camSeconds}s</span>
                  </div>
                  <button onClick={() => { setCamModal(false); setCamActive(false); }} className="w-full py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}>{lang === 'zh' ? '关闭' : 'Close'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
